import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import authService from '../services/authService';
import { useTranslation } from '../../store/languageStore';
import { colors, brand } from '../../theme';

interface ModuleAuthModalProps {
  open: boolean;
  onClose: (authenticated?: boolean) => void;
  moduleId: string;
  moduleName: string;
}

interface AuthFormData {
  username: string;
  password: string;
}

const ModuleAuthModal: React.FC<ModuleAuthModalProps> = ({
  open,
  onClose,
  moduleId,
  moduleName
}) => {
  const { tString } = useTranslation();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Create schema with translations
  const schema = yup.object({
    username: yup.string().required(tString('documentProcessing.moduleAuth.usernameRequired')),
    password: yup.string().required(tString('documentProcessing.moduleAuth.passwordRequired')),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AuthFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: AuthFormData) => {
    try {
      setIsLoading(true);
      
      console.log(`Authenticating with module ${moduleName} (${moduleId})`);
      
      const result = await authService.authenticateModule(moduleId, {
        username: data.username,
        password: data.password,
      });

      console.log('Authentication result:', result);

      // Show detailed success message
      let successMessage = tString('documentProcessing.moduleAuth.authSuccess', moduleName);
      if (result.user_details_saved) {
        successMessage += ' ' + tString('documentProcessing.moduleAuth.userDetailsSaved');
      } else if (result.user_details_error) {
        console.warn('User details fetch error:', result.user_details_error);
      }

      toast.success(successMessage);
      reset();
      onClose(true); // Pass true to indicate successful authentication

    } catch (error: any) {
      console.error('Module authentication error:', error);

      let errorMessage = tString('documentProcessing.moduleAuth.authFailed');

      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: brand.yellow,
          color: brand.black,
          py: 2,
          px: 3,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <SecurityIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight={600}>
            {tString('documentProcessing.moduleAuth.title', moduleName)}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={isLoading}
          sx={{
            color: brand.black,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ py: 4, px: 3 }}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Controller
              name="username"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={tString('documentProcessing.moduleAuth.username')}
                  placeholder={tString('documentProcessing.moduleAuth.usernamePlaceholder')}
                  error={!!errors.username}
                  helperText={errors.username?.message}
                  fullWidth
                  disabled={isLoading}
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={tString('documentProcessing.moduleAuth.password')}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={tString('documentProcessing.moduleAuth.passwordPlaceholder')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  fullWidth
                  disabled={isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: theme.palette.primary.main,
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isLoading}
                          sx={{
                            color: theme.palette.primary.main,
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(135deg, ${colors.gradientStart}08 0%, ${colors.gradientEnd}08 100%)`,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}10`,
              },
            }}
          >
            {tString('documentProcessing.moduleAuth.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SecurityIcon />}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              px: 3,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              '&.Mui-disabled': {
                background: theme.palette.grey[300],
                color: theme.palette.grey[500],
              },
            }}
          >
            {isLoading ? tString('documentProcessing.moduleAuth.authenticating') : tString('documentProcessing.moduleAuth.authenticate')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ModuleAuthModal;